/**
 * This includes classes for tracking interactions with creatives. The data
 * they collect are attached as query parameters to clicks (the last
 * interaction prior to a click is assumed to be the click itself).
 *
 * We listen for all three possible types of events (MouseEvents,
 * PointerEvents, and TouchEvents), because 1) device capabilities differ, so
 * different events may fire on different devices, and 2) some third-party
 * creatives block propagation of some events such that later events do not
 * fire (e.g. if they stop propagation of a PointerEvent, then the
 * corresponding TouchEvent will not fire). However, we collect data from
 * only one type of event in the following order of priority: 1) touch,
 * 2) pointer, 3) mouse (e.g. we only collect pointer data if we're not
 * receiving touch events).
 *
 * We somewhat arbitrarily define interactions as starting and ending at
 * the window's boundaries (i.e. if the user moves over a path that leaves
 * and then re-enters window, this would count as two interactions: one ends
 * when their finger leaves the window, and another one starts when it
 * re-enters the window).
 */
import { deepEqual } from "./frontendlib/utils";

const POINTER_TYPE_TOUCH = 1;
const POINTER_TYPE_MOUSE = 2;
// POINTER_TYPE_POINTER is a catch-all for anything other than touch or mouse.
const POINTER_TYPE_POINTER = 3;

/**
 * Returns the pointer type of a PointerEvent.
 */
const getPointerEventPointerType = (pointerEvent) => {
  switch (pointerEvent.pointerType) {
    case "mouse":
      return POINTER_TYPE_MOUSE;
    case "touch":
      return POINTER_TYPE_TOUCH;
    default:
      return POINTER_TYPE_POINTER;
  }
};

/**
 * Given a MouseEvent or a PointerEvent, returns true iff a device button
 * is pressed (returns false if this cannot be determined from the event).
 */
const isButtonPressed = (e) => {
  // e.which is a non-standard fallback for old versions of Safari.
  const buttons = e.buttons != null ? e.buttons : e.which;
  return buttons != null && buttons !== 0;
};

/** Returns the linear distance between (startX, startY) and (endX, endY). */
const computeDistance = (startX, startY, endX, endY) => {
  return Math.sqrt(Math.pow(startX - endX, 2) + Math.pow(startY - endY, 2));
};

/**
 * Returns true iff the given TouchEvent has some touch-point within the bounds
 * of the window.
 */
const isTouchInWindow = (e) => {
  return [...e.changedTouches].some((touch) => {
    const { clientX, clientY } = touch;
    return (
      clientX >= 0 &&
      clientX <= window.innerWidth &&
      clientY >= 0 &&
      clientY <= window.innerHeight
    );
  });
};

/**
 * Returns the maximum force of all touches in a TouchEvent, or 0 if force
 * information is not available.
 */
const maxTouchEventForce = (e) => {
  const touchForces = [...e.changedTouches, ...e.touches].map(
    (touch) => touch.force || 0
  );

  return Math.max(0, ...touchForces);
};

/**
 * Returns the total touch area of a TouchEvent, or 0 if touch area is not
 * available.
 */
const touchEventArea = (e) => {
  return [...e.touches].reduce((sum, touch) => {
    const { radiusX, radiusY } = touch;
    const area =
      radiusX == null || radiusY == null ? 0 : Math.PI * radiusX * radiusY;
    return sum + area;
  }, 0);
};

class Interaction {
  constructor(startEvent) {
    this._startEvent = startEvent;
    this._lastMoveEvent = null;
    this._endEvent = null;
    this._maxForce = startEvent.force;
    this._maxArea = startEvent.area;
    this._totalDistance = 0;
  }

  setEndEvent(endEvent) {
    this._updateMetrics(endEvent);
    this._endEvent = endEvent;
  }

  setLastMoveEvent(moveEvent) {
    this._updateMetrics(moveEvent);
    this._lastMoveEvent = moveEvent;
  }

  isInProgress() {
    return this._startEvent != null && this._endEvent == null;
  }

  isComplete() {
    return this._startEvent != null && this._endEvent != null;
  }

  getMetrics() {
    const metrics = {
      startX: this._startEvent.x,
      startY: this._startEvent.y,
      isTrusted: this._startEvent.isTrusted,
      maxForce: this._maxForce,
      maxArea: this._maxArea,
      distance: this._totalDistance,
      pointerType: this._startEvent.pointerType,
      // Omit durationMS because a null duration is different
      // than a duration of 0, it's important to distinguish the two.
      // The nullable value is allowed by the rtb.proto.Click object.
    };

    if (this.isComplete()) {
      // Add data that requires the interaction to be complete,
      // (i.e. has a end event).
      Object.assign(metrics, {
        durationMS: this._endEvent.timestamp - this._startEvent.timestamp,
      });
    }

    return metrics;
  }

  _updateMetrics(newEvent) {
    this._maxForce = Math.max(this._maxForce, newEvent.force);
    this._lastMaxArea = Math.max(this._maxArea, newEvent.area);

    const lastEvent = this._lastMoveEvent || this._startEvent;
    this._totalDistance += computeDistance(
      lastEvent.x,
      lastEvent.y,
      newEvent.x,
      newEvent.y
    );
  }
}

/**
 * This is an abstract base class for monitoring interaction events. Concrete
 * subclasses must define two methods:
 *
 * 1) _attachEventListeners: this must attach the relevant listeners to the
 * window and call _handleStartEvent, _handleEndEvent, and _handleMoveEvent
 * with the proper raw event.
 * 2) _normalizeEvent: this must take a raw event of the relevant type and
 * return a "normalized event" (an object like { x, y, isTrusted, timestamp,
 * force, area, pointerType }.
 */
class InteractionEventMonitor {
  // #if process.env.NODE_ENV === "test"
//   destroy() {
//     this._attachedEventListeners.forEach(
//       ({ eventName, handler, listenerOptions }) => {
//         window.removeEventListener(eventName, handler, listenerOptions);
//       }
//     );
//     this._attachedEventListeners = [];
//   }
  // #endif

  constructor() {
    this._interactionCount = 0;
    this._totalInteractionDistance = 0;
    this._lastInteraction = null;
    this._interactionStartHandlers = [];
    this._interactionEndHandlers = [];
    this._attachedEventListeners = [];
    this._attachEventListeners();
  }

  listenFor(eventName, handler, options = {}) {
    const listenerOptions = {
      // NOTE(Austin): By default, it's absolutely critical we capture events
      // as they bubble down so we have interaction data before developers
      // presumably call our `Liftoff.open` method in an event listener handler.
      capture: true,
      ...options,
    };
    this._attachedEventListeners.push({
      eventName,
      handler,
      listenerOptions,
    });
    window.addEventListener(eventName, handler, listenerOptions);
  }

  onInteractionStart(handlerFn) {
    if (this._interactionStartHandlers.includes(handlerFn)) {
      return;
    }

    this._interactionStartHandlers.push(handlerFn);
  }

  _handleStartEvent(e) {
    if (this.isInteractionInProgress()) {
      // This could occur if another interaction starts before another one
      // completes (e.g. in the case of multiple pointer devices) or if the
      // device fires multiple 'start' events in some cases (e.g. firing
      // "pointerover" and then "pointerdown" for touch events).
      return;
    }

    const normalizedEvent = this._normalizeEvent(e);
    this._lastInteraction = new Interaction(normalizedEvent);
    this._interactionStartHandlers.forEach((handler) => handler());
  }

  onInteractionEnd(handlerFn) {
    if (this._interactionEndHandlers.includes(handlerFn)) {
      return;
    }

    this._interactionEndHandlers.push(handlerFn);
  }

  _handleEndEvent(e) {
    if (!this.isInteractionInProgress()) {
      return;
    }
    const normalizedEvent = this._normalizeEvent(e);
    this._lastInteraction.setEndEvent(normalizedEvent);

    this._updateCumulativeInteractionStats();
    this._interactionEndHandlers.forEach((handler) => handler());
  }

  _handleMoveEvent(e) {
    if (!this.isInteractionInProgress()) {
      return;
    }

    const normalizedEvent = this._normalizeEvent(e);
    this._lastInteraction.setLastMoveEvent(normalizedEvent);
  }

  isInteractionInProgress() {
    return (
      this._lastInteraction != null && this._lastInteraction.isInProgress()
    );
  }

  isLastInteractionComplete() {
    return this._lastInteraction != null && this._lastInteraction.isComplete();
  }

  getLastInteractionData() {
    return this._lastInteraction && this._lastInteraction.getMetrics();
  }

  getAllInteractionData() {
    return {
      lastInteraction: this.getLastInteractionData(),
      cumulativeStats: this.getCumulativeInteractionStats(),
    };
  }

  _updateCumulativeInteractionStats() {
    this._interactionCount += 1;
    this._totalInteractionDistance +=
      this._lastInteraction.getMetrics().distance;
  }

  getCumulativeInteractionStats() {
    return {
      interactionCount: this._interactionCount,
      totalDistance: this._totalInteractionDistance,
    };
  }

  _normalizeEvent(_) {
    throw new Error(
      "_normalizeEvent must be implemented by a concrete subclass"
    );
  }

  _attachEventListeners() {
    throw new Error(
      "_attachEventListeners must be implemented by a concrete subclass"
    );
  }
}

class MouseEventMonitor extends InteractionEventMonitor {
  constructor() {
    super();
  }

  _normalizeEvent(e) {
    return {
      x: e.pageX,
      y: e.pageY,
      isTrusted: e.isTrusted,
      timestamp: e.timeStamp,
      // NOTE(Casey): MouseEvent.webkitForce is non-standard, but we collect
      // it if it's available anyway.
      force: e.webkitForce || 0,
      area: 0,
      pointerType: POINTER_TYPE_MOUSE,
    };
  }

  _attachEventListeners() {
    super.listenFor("mousedown", (e) => this._handleStartEvent(e));
    super.listenFor("mouseover", (e) => {
      if (!isButtonPressed(e)) return;
      this._handleStartEvent(e);
    });
    super.listenFor("mouseup", (e) => this._handleEndEvent(e));
    super.listenFor("mouseout", (e) => {
      if (!isButtonPressed(e)) return;
      this._handleEndEvent(e);
    });
    super.listenFor("mousemove", (e) => {
      if (!isButtonPressed(e)) return;
      this._handleMoveEvent(e);
    });
  }
}

class PointerEventMonitor extends InteractionEventMonitor {
  constructor() {
    super();
  }

  _normalizeEvent(e) {
    return {
      x: e.pageX,
      y: e.pageY,
      isTrusted: e.isTrusted,
      timestamp: e.timeStamp,
      force: e.pressure,
      // NOTE(Casey): We consider pointer area as an ellipse for consistency
      // with touch events.
      area: (Math.PI * e.width * e.height) / 4,
      pointerType: getPointerEventPointerType(e),
    };
  }

  _attachEventListeners() {
    super.listenFor("pointerdown", (e) => this._handleStartEvent(e));
    super.listenFor("pointerover", (e) => {
      if (!isButtonPressed(e)) return;
      this._handleStartEvent(e);
    });
    super.listenFor("pointerup", (e) => this._handleEndEvent(e));
    super.listenFor("pointercancel", (e) => this._handleEndEvent(e));
    super.listenFor("pointerout", (e) => {
      if (!isButtonPressed(e)) return;
      this._handleEndEvent(e);
    });
    super.listenFor("pointermove", (e) => {
      if (!isButtonPressed(e)) return;
      this._handleMoveEvent(e);
    });
  }
}

class TouchEventMonitor extends InteractionEventMonitor {
  constructor() {
    super();
  }

  _normalizeEvent(e) {
    return {
      // NOTE(Casey): We arbitrarily choose a changed touch to use for the
      // interaction position, making distance measures inaccurate for multi-
      // touch interactions.
      x: e.changedTouches[0].pageX,
      y: e.changedTouches[0].pageY,
      isTrusted: e.isTrusted,
      timestamp: e.timeStamp,
      force: maxTouchEventForce(e),
      area: touchEventArea(e),
      pointerType: POINTER_TYPE_TOUCH,
    };
  }

  _attachEventListeners() {
    super.listenFor("touchstart", (e) => this._handleStartEvent(e));
    super.listenFor("touchend", (e) => {
      if (e.touches.length !== 0) return;
      this._handleEndEvent(e);
    });
    super.listenFor("touchcancel", (e) => this._handleEndEvent(e));
    super.listenFor("touchmove", (e) => {
      // NOTE(Casey): There are no touch events analogous to
      // mouseover/pointerover and mouseout/pointerout, so we check if the touch
      // is within the window during move events instead.
      const isInWindow = isTouchInWindow(e);
      if (!isInWindow && this.isInteractionInProgress()) {
        // The touch started inside the window and has now moved outside of it.
        this._handleEndEvent(e);
      } else if (isInWindow && this.isLastInteractionComplete()) {
        // The touch started outside of the window and moved into the window,
        // so we need to start tracking a new interaction.
        this._handleStartEvent(e);
      } else {
        // This event is part of the interaction we're currently tracking.
        this._handleMoveEvent(e);
      }
    });
  }
}

class InteractionMonitor {
  // #if process.env.NODE_ENV === "test"
//   destroy() {
//     this._monitors.forEach((monitor) => {
//       monitor.destroy();
//     });
//     this._monitors = [];
//   }
  // #endif

  constructor() {
    // These events are ordered by priority, from highest to lowest.
    this._monitors = [
      new TouchEventMonitor(),
      new PointerEventMonitor(),
      new MouseEventMonitor(),
    ];

    this._monitors.forEach((monitor) => {
      monitor.onInteractionStart(this._handleInteractionEvent.bind(this));
    });
    this._monitors.forEach((monitor) => {
      monitor.onInteractionEnd(this._handleInteractionEvent.bind(this));
    });

    this._previousAllInteractionData = null;
    this._interactionChangeHandlers = [];
  }

  /**
   * Given a function that will receive an object like that returned by
   * getAllInteractionData, sets up listeners so that handlerFn will be called
   * whenever any interaction data change.
   */
  onInteractionChange(handlerFn) {
    if (this._interactionChangeHandlers.includes(handlerFn)) {
      return;
    }

    this._interactionChangeHandlers.push(handlerFn);
  }

  _handleInteractionEvent() {
    const currentAllInteractionData = this.getAllInteractionData();
    if (
      deepEqual(this._previousAllInteractionData, currentAllInteractionData)
    ) {
      return;
    }

    this._previousAllInteractionData = currentAllInteractionData;
    this._interactionChangeHandlers.forEach((handler) => {
      handler(currentAllInteractionData);
    });
  }

  _chooseEventMonitorWithInProgressInteractions() {
    return this._monitors.find((eventMonitor) =>
      eventMonitor.isInteractionInProgress()
    );
  }

  _chooseEventMonitorWithCompletedInteractions() {
    return this._monitors.find((eventMonitor) =>
      eventMonitor.isLastInteractionComplete()
    );
  }

  _chooseEventMonitor() {
    // Since this class is used by in-house and third-party creatives
    // we prioritize partial interactions (i.e. clickthrough events
    // that fire before the touchEnd/clickEnd/etc. fires).
    return (
      this._chooseEventMonitorWithInProgressInteractions() ||
      this._chooseEventMonitorWithCompletedInteractions()
    );
  }

  /**
   * Returns a map containing the last interaction and the cumulative
   * statistics. Prefer using this over calling `getLastInteractionData` and
   * `getCumulativeInteractionStats` separately.
   */
  getAllInteractionData() {
    const eventMonitor = this._chooseEventMonitor();
    if (eventMonitor == null) {
      return null;
    }

    return eventMonitor.getAllInteractionData();
  }

  /**
   * Returns an object like { startX, startY, isTrusted, durationMS, maxForce
   * maxArea, distance, pointerType } describing the last interaction.
   */
  getLastInteractionData() {
    const eventMonitor = this._chooseEventMonitor();
    if (eventMonitor == null) {
      return null;
    }

    return eventMonitor.getLastInteractionData();
  }

  /** Returns an object like { interactionCount, totalDistance }. */
  getCumulativeInteractionStats() {
    const eventMonitor = this._chooseEventMonitor();
    if (eventMonitor == null) {
      return null;
    }

    return eventMonitor.getCumulativeInteractionStats();
  }
}

export {
  InteractionMonitor,
  // #if process.env.NODE_ENV === "test"
//   POINTER_TYPE_TOUCH,
//   POINTER_TYPE_MOUSE,
//   POINTER_TYPE_POINTER,
  // #endif
};



// WEBPACK FOOTER //
// ./src/js/interaction_monitor.js